import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';
import {useNavigate} from 'react-router-dom';
import {enqueueSnackbar} from 'notistack';
import {
  createStripeCustomer,
  formatCents,
  formatDay,
  getBilling,
  planLabel,
  statusLabel,
  statusTone,
  syncBilling,
  usageLabel,
} from './billing.service';
import {AdminBillingDto, AdminCompanyBillingDto} from './dtos/admin-billing.dto';
import {isAuthRedirectError} from '../../shared/services/auth.service';
import {logError} from '../../shared/services/logger.service';

const matches = (company: AdminCompanyBillingDto, text: string) => {
  const needle = text.trim().toLowerCase();
  if (!needle) return true;
  return [company.name, company.email, company.ownerName, company.ownerEmail, company.stripeCustomerId]
    .some(value => value?.toLowerCase().includes(needle));
};

const paymentLine = (company: AdminCompanyBillingDto) => {
  const payment = company.lastPayment;
  if (!payment) return '—';
  const amount = formatCents(payment.amountCents);
  return payment.status === 'failed' ? `Failed ${amount} on ${formatDay(payment.at)}` : `${amount} on ${formatDay(payment.at)}`;
};

const periodLine = (company: AdminCompanyBillingDto) => {
  const {periodStart, periodEnd} = company.usage;
  return periodEnd ? `${formatDay(periodStart)} – ${formatDay(periodEnd)}` : `since ${formatDay(periodStart)}`;
};

// SQ-415: every company's Stripe billing at a glance, for SavaQuan staff.
// The server keeps a mirror of Stripe (company_billing) that its webhook
// writes; this page reads the mirror and the estimate count. Create
// customer puts the company in Stripe (with company_id metadata, so
// Stripe's events find it); plans and cards are taken on Stripe's own pages
// until the apps' Account screens are pointed at the server. Sync re-reads
// a customer's subscriptions when a webhook was missed.
const BillingTab: React.FC = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState<AdminBillingDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [busyCompany, setBusyCompany] = useState<number | null>(null);

  const failed = useCallback((context: string, e: unknown) => {
    if (isAuthRedirectError(e)) return;
    logError(context, {}, e as Error);
    enqueueSnackbar((e as Error).message, {variant: 'error', autoHideDuration: 8000});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setBilling(await getBilling(navigate));
    } catch (e) {
      failed('Billing list failed', e);
    } finally {
      setLoading(false);
    }
  }, [navigate, failed]);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (updated: AdminCompanyBillingDto) =>
    setBilling(current => current && {
      ...current,
      companies: current.companies.map(c => c.companyId === updated.companyId ? updated : c),
    });

  const act = async (company: AdminCompanyBillingDto, action: 'create' | 'sync') => {
    setBusyCompany(company.companyId);
    try {
      if (action === 'create') {
        replace(await createStripeCustomer(navigate, company.companyId));
        enqueueSnackbar(`Created a Stripe customer for ${company.name}.`, {variant: 'success'});
      } else {
        replace(await syncBilling(navigate, company.companyId));
        enqueueSnackbar(`Synced ${company.name} from Stripe.`, {variant: 'success'});
      }
    } catch (e) {
      failed(action === 'create' ? 'Stripe customer create failed' : 'Billing sync failed', e);
    } finally {
      setBusyCompany(null);
    }
  };

  const companies = useMemo(
    () => (billing?.companies ?? []).filter(c => matches(c, filter)),
    [billing, filter]);

  const stripe = billing?.stripe;
  const canCallStripe = stripe?.enabled === true && stripe.configured;

  return (
    <Box sx={{p: 3}}>
      {stripe && !canCallStripe && (
        <Alert severity="warning" sx={{mb: 2}}>
          Stripe cannot be called from this server. {stripe.problem}
        </Alert>
      )}
      {stripe && canCallStripe && stripe.liveMode === false && (
        <Alert severity="info" sx={{mb: 2}}>
          This server uses a Stripe <strong>test</strong> key: nothing here charges anyone.
        </Alert>
      )}

      <Box sx={{display: 'flex', gap: 1, alignItems: 'center', mb: 2}}>
        <TextField
          label="Company, owner or Stripe id"
          value={filter}
          onChange={event => setFilter(event.target.value)}
          size="small"
          sx={{flex: 1, maxWidth: 420}}
          autoComplete="off"
        />
        <Button variant="outlined" onClick={() => void load()} disabled={loading} startIcon={<SyncIcon/>}>
          Refresh
        </Button>
      </Box>

      {loading && !billing && <CircularProgress sx={{m: 3}}/>}

      {billing && companies.length === 0 && (
        <Typography color="text.secondary">
          {billing.companies.length === 0 ? 'There are no companies yet.' : 'No company matches that.'}
        </Typography>
      )}

      {companies.length > 0 && (
        <TableContainer>
          <Table size="small" aria-label="company billing">
            <TableHead>
              <TableRow>
                <TableCell>Company</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Estimates used</TableCell>
                <TableCell>Last payment</TableCell>
                <TableCell>Stripe</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {companies.map(company => {
                const busy = busyCompany === company.companyId;
                return (
                  <TableRow key={company.companyId} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{company.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {company.memberCount} {company.memberCount === 1 ? 'member' : 'members'} · since {formatDay(company.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{company.ownerName ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">{company.ownerEmail ?? company.email ?? ''}</Typography>
                    </TableCell>
                    <TableCell>{planLabel(company.subscription)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(company.subscription)}
                        color={statusTone(company.subscription)}
                        variant={company.subscription ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" noWrap>{periodLine(company)}</Typography>
                    </TableCell>
                    <TableCell align="right">{usageLabel(company.usage)}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color={company.lastPayment?.status === 'failed' ? 'error' : 'text.primary'}
                        noWrap
                      >
                        {paymentLine(company)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {company.stripeCustomerId ? (
                        <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                          {company.stripeCustomerUrl ? (
                            <Link href={company.stripeCustomerUrl} target="_blank" rel="noopener" variant="body2" noWrap>
                              {company.stripeCustomerId}
                            </Link>
                          ) : (
                            <Typography variant="body2" noWrap>{company.stripeCustomerId}</Typography>
                          )}
                          {company.stripeCustomerUrl && (
                            <IconButton
                              size="small"
                              component="a"
                              href={company.stripeCustomerUrl}
                              target="_blank"
                              rel="noopener"
                              aria-label={`Open ${company.name} in Stripe`}
                            >
                              <OpenInNewIcon fontSize="inherit"/>
                            </IconButton>
                          )}
                          <Tooltip title="Re-read this customer's subscriptions from Stripe">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => void act(company, 'sync')}
                                disabled={busy || !canCallStripe}
                                aria-label={`Sync ${company.name}`}
                              >
                                {busy ? <CircularProgress size={14}/> : <SyncIcon fontSize="inherit"/>}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => void act(company, 'create')}
                          disabled={busy || !canCallStripe}
                        >
                          {busy ? <CircularProgress size={14} sx={{mr: 1}}/> : null}
                          Create Stripe customer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BillingTab;
